import { GetWSMgr } from "@/Utils/StateLib";
import { Alert, Flex } from "@theorgchartdev/storybook";
import { BackgroundAreas } from "@/appComponents/Navigation/RightSidebar/BackgroundDisplay/BackgroundAreas";
import React from "react";
import { getCurrentAppliedBackground } from "@/appComponents/Navigation/RightSidebar/BackgroundDisplay/Backgrounds.utils";
import { OCLink } from "@/appComponents/Blocks/Utils/OCLink";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import {
  RSBFooter,
  RSBPrimaryButton,
  RSBSecondaryButton
} from "@/appComponents/Navigation/RightSidebar/Utils/RSBFooter";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { chartModeTestId } from "@/appComponents/Navigation/RightSidebar/Utils/chartModeTestId";

type BackgroundElementsProps = {
  doc: QXDoc;
  currentBackgroundName: string;
  backgroundArea: any;
  backgroundCtrl: any;
};

export const BackgroundElements = ({
  doc,
  currentBackgroundName,
  backgroundArea,
  backgroundCtrl
}: BackgroundElementsProps) => {
  const { canModifyBackgrounds } = usePermissionCheck();

  const [defaultBackground, _setDefaultBG] = React.useState(() => {
    const iWiz = doc?.GetIWizData();
    return iWiz?.GetDefaultBackground();
  });

  const chartID = doc?.GetChartID();
  const chartLink = "/settings/chart/" + chartID + "/presentation";
  const filename = getCurrentAppliedBackground() ?? "None";

  const showLinkedAlert = () => {
    const wsMgr = GetWSMgr();
    if (!Globals._uiConfig.InBackgroundEditorMode()) return false;
    if (!doc.IsServerChart()) return false;
    if (!wsMgr || wsMgr.IsBackgroundDirty()) return false;
    return !(!filename || filename === "None");
  };

  const handleSaveAs = () => {
    if (!canModifyBackgrounds) return;
    const wsMgr = GetWSMgr();
    wsMgr?.DoSaveBackground(true);
  };

  const handleDiscard = () => {
    const wsMgr = GetWSMgr();
    if (!wsMgr) return;
    const masterFile = doc?.GetIWizData?.()?.GetMasterFile?.();
    wsMgr.discardBackgroundEditorChanges();
    // Only re-broadcast areas when there's a linked master file to restore.
    if (masterFile) {
      const unsub = wsMgr.SubscribeToTopic("BackgroundNameChange", ({ name }: { name: string }) => {
        if (name && name !== "None") {
          wsMgr.fBackgroundMgr?.BroadcastCurrentAreas();
          unsub();
        }
      });
    }
  };

  return (
    <Flex direction={"column"} width={"100%"} height={"100%"} overflow={"hidden"}>
      <Flex direction={"column"} gap={"8px"} flex={"1"} padding={"0 16px"} overflow={"hidden"}>
        {Globals._uiConfig.InBackgroundEditorMode() && (
          <BackgroundAreas
            backgroundCtrl={backgroundCtrl}
            backgroundArea={backgroundArea}
            backgroundName={currentBackgroundName}
          />
        )}
        {showLinkedAlert() && (
          <Flex width={"100%"} direction={"column"} gap={"12px"} margin={"auto 0 12px 0"}>
            <Alert
              severity={"warning"}
              withBorder
              showIcon
              className={"alert-small-text"}
              alertActions={{ closeAction: () => {} }}
            >
              {appstr("Editing linked background") + " "}
              {defaultBackground !== filename && (
                <OCLink className="ocn-background-alert-link" to={chartLink} onClick={undefined}>
                  {appstr("Set default background")}
                </OCLink>
              )}
            </Alert>
          </Flex>
        )}
      </Flex>
      {Globals._uiConfig.InBackgroundEditorMode() && (
        <RSBFooter>
          {doc?.IsServerChart() && (
            <RSBSecondaryButton
              onClick={handleDiscard}
              dataTestId={chartModeTestId("sidebar", "background-elements-discard-btn")}
            />
          )}
          <RSBPrimaryButton
            data-testid="background--save-as-btn"
            onClick={handleSaveAs}
            disabled={!canModifyBackgrounds}
          >
            {appstr("saveas (short)")}
          </RSBPrimaryButton>
        </RSBFooter>
      )}
    </Flex>
  );
};
